import {
  defaultLoadableState,
  type LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import { createContext, useCallback, useMemo, useState } from 'react';
import type React from 'react';
import { getIsSpinnerDisabled } from '~/selectors';
import useSelector from '~/store/useSelector';
import EventManager from './EventManager';
import useEventTrigger from './useEventTrigger';

type DummyFnType = () => void;

type ContextType = {
  availabilityUpdateFlag: number;
  favoriteUpdateFlag: number;
  headerHeight: number;
  logInFlag: number;
  logOutFlag: number;
  openImmersive: () => void;
  setHeaderHeight: React.Dispatch<React.SetStateAction<number>>;
  setOpenImmersive: React.Dispatch<React.SetStateAction<DummyFnType>>;
  spinnerReady: LoadableState<boolean>;
  triggerEvent: EventManager;
  triggerFetchUserProfile: () => void;
};

const defaultContext: ContextType = {
  availabilityUpdateFlag: 0,
  favoriteUpdateFlag: 0,
  headerHeight: 112,
  logInFlag: 0,
  logOutFlag: 0,
  openImmersive() {},
  setHeaderHeight() {},
  setOpenImmersive() {},
  spinnerReady: defaultLoadableState(false),
  triggerEvent: new EventManager(false),
  triggerFetchUserProfile() {}
};

const GlobalContext = createContext(defaultContext);

export function GlobalContextProvider({ children }) {
  const eventManager = useMemo(() => new EventManager(true), []);
  const availabilityUpdateFlag = useEventTrigger(eventManager, 'availability');
  const favoriteUpdateFlag =
    useEventTrigger(eventManager, 'favoriteAdd') -
    useEventTrigger(eventManager, 'favoriteRemove');
  const [headerHeight, setHeaderHeight] = useState<number>(
    defaultContext.headerHeight
  );
  const remoteUserRequestFlag = useEventTrigger(eventManager, 'logIn');
  const remoteLogOutFlag = useEventTrigger(eventManager, 'logOut');
  const [openImmersive, setOpenImmersive] = useState<DummyFnType>(
    () => defaultContext.openImmersive
  );
  const [localUserRequestFlag, setLocalUserRequestFlag] = useState(0);

  // TODO: Someday pass this into GlobalContextProvider as a prop instead of reading from Redux
  const isSpinnerDisabled = useSelector(getIsSpinnerDisabled);
  const spinnerReady = useLoadableState(isSpinnerDisabled, {
    initializeAsLoaded: isSpinnerDisabled
  });

  const triggerFetchUserProfile = useCallback(() => {
    setLocalUserRequestFlag(Date.now());
  }, []);

  const value = useMemo<ContextType>(
    () => ({
      availabilityUpdateFlag,
      favoriteUpdateFlag,
      headerHeight,
      logInFlag: remoteUserRequestFlag - localUserRequestFlag, // Using subtraction to avoid integer overflow
      logOutFlag: remoteLogOutFlag,
      openImmersive,
      setHeaderHeight,
      setOpenImmersive,
      spinnerReady,
      triggerEvent: eventManager,
      triggerFetchUserProfile
    }),
    [
      availabilityUpdateFlag,
      eventManager,
      favoriteUpdateFlag,
      headerHeight,
      localUserRequestFlag,
      openImmersive,
      remoteLogOutFlag,
      remoteUserRequestFlag,
      spinnerReady,
      triggerFetchUserProfile
    ]
  );

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
}

GlobalContextProvider.displayName = 'GlobalContextProvider';

export default GlobalContext;
